import { apiClient } from "./client";

export interface DualSearchResultItem {
  type: string;
  data: {
    id?: string;
    internalId?: string;
    externalId?: string;
    [key: string]: unknown;
  };
}

export interface DualSearchResult {
  id: string;
  externalId: string;
  type: "account" | "application" | "transaction" | "refund";
  status?: string;
  title: string;
  subtitle: string;
  [key: string]: unknown;
}

function mapSearchItemToResult(item: DualSearchResultItem): DualSearchResult | null {
  const normalizedType = item.type.toLowerCase();
  const validTypes = ["account", "application", "transaction", "refund"];

  if (!validTypes.includes(normalizedType)) {
    if (import.meta.env.DEV) {
      console.warn(`[search] Unknown search result type: ${item.type}`);
    }
    return null;
  }

  const type = normalizedType as "account" | "application" | "transaction" | "refund";
  const data = item.data;

  const id = (data.id || data.internalId || "") as string;
  if (!id) {
    return null;
  }

  let externalId = (data.externalId as string) || id;
  let status = (data.status as string) || "";
  let title = "";
  let subtitle = "";

  if (type === "account") {
    externalId = id;
    status = (data.status as string) || "";
    const zip = (data.zip as string) || (data.zipCode as string) || "";
    const ssn = (data.ssn as string) || (data.SSN as string) || "";
    const phone = (data.phone as string) || "";
    title = zip || `Account ${id.slice(0, 8)}`;
    subtitle = ssn || phone || "";
  } else if (type === "application") {
    externalId = (data.externalId as string) || id;
    status = (data.status as string) || "";
    const firstName = (data.firstName as string) || "";
    const lastName = (data.lastName as string) || "";
    title =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : externalId || `Application ${id.slice(0, 8)}`;
    const amount = data.amount as number | undefined;
    subtitle = data.email
      ? (data.email as string)
      : amount !== undefined && amount > 0
        ? `$${((amount as number) / 100).toFixed(2)}`
        : "";
  } else if (type === "transaction") {
    externalId = (data.externalId as string) || id;
    status = (data.status as string) || "";
    title = externalId || `Transaction ${id.slice(0, 8)}`;
    const amount = data.amount as number | undefined;
    subtitle = amount !== undefined ? `$${((amount as number) / 100).toFixed(2)}` : "";
  } else if (type === "refund") {
    externalId = (data.externalId as string) || id;
    status = (data.status as string) || "";
    title = externalId || `Refund ${id.slice(0, 8)}`;
    const amount = data.amount as number | undefined;
    subtitle = amount !== undefined ? `$${((amount as number) / 100).toFixed(2)}` : "";
  }

  const { id: _, internalId: __, type: ___, ...restData } = data;
  return {
    id,
    externalId: externalId || id,
    type,
    status,
    title,
    subtitle,
    ...restData,
  };
}

export const searchApi = {
  dualSearch: async (searchId: string): Promise<DualSearchResult[]> => {
    const response = await apiClient.get<DualSearchResultItem[]>(
      `/dual_search/${encodeURIComponent(searchId)}`
    );
    const results = response.data;
    if (!results || results.length === 0) {
      return [];
    }

    return results
      .map(mapSearchItemToResult)
      .filter((result): result is DualSearchResult => result !== null);
  },
};
